import React from "react"
import PropTypes from "prop-types"
import {
  SettingsCard,
  SettingsCardTitle,
  SettingsCardContent,
} from "@modules/site/settings"
import { SiteSettingsSection, SiteSettingsSectionLabel } from "../constants"
import IncrementalBuildsSettings from "./IncrementalBuildsSettings"
import { EnableBuilds } from "./EnableBuilds"
import { EnableBurstBuilds } from "./EnableBurstBuilds"
import { EnableIncrementalDeploysProduction } from "./EnableIncrementalDeploysProduction"
import { EnableMerlinPullRequestsSourcing } from "./EnableMerlinPullRequestsSourcing"
import { EnableSlicesAPI } from "./EnableSlicesAPI"
import { EnableGatsbyjsio } from "./EnableGatsbyjsio"
import EnableBuildChecks from "./EnableBuildChecks"
import { EnableAutomaticDeploys } from "./EnableAutomaticDeploys"
import { CdnVendor, MachinePricingTier } from "@modules/graphql/types"
import { isProperlyConnected } from "@modules/site/cdnIntegrations"
import { useLocalSiteDetailsForBuilds } from "@modules/site/shared/hooks/useLocalSiteDetails"
import { EnablePreviewBuilds } from "./EnablePreviewBuilds"
import { useFlags } from "@modules/featureFlags"

const cardCss = theme => ({
  display: `grid`,
  gridGap: theme.space[7],
})

const propTypes = {
  siteId: PropTypes.string.isRequired,
  organizationId: PropTypes.string.isRequired,
}

export default function BuildsSection({ siteId, organizationId, tier }) {
  const { flags } = useFlags()
  const siteData = useLocalSiteDetailsForBuilds(siteId)
  const cdnIntegrations = siteData?.cdnIntegrations ?? []
  const activeIntegration = cdnIntegrations.find(isProperlyConnected)
  const gatsbyHostingOn = activeIntegration?.vendor === CdnVendor.CloudCdn

  if (!siteData) {
    return null
  }

  const buildsEnabled = siteData.buildsEnabled

  return (
    <React.Fragment>
      <SettingsCard id={SiteSettingsSection.BuildTypes}>
        <SettingsCardTitle>
          {SiteSettingsSectionLabel[SiteSettingsSection.BuildTypes]}
        </SettingsCardTitle>
        <SettingsCardContent css={cardCss}>
          <EnableBuilds
            siteId={siteId}
            buildsEnabled={siteData.buildsEnabled}
          />
          <EnablePreviewBuilds
            siteId={siteId}
            previewBuildsEnabled={siteData.previewBuildsEnabled ?? false}
            disabled={typeof siteData.previewBuildsEnabled !== `boolean`}
          />
        </SettingsCardContent>
      </SettingsCard>
      {buildsEnabled && (
        <SettingsCard id={SiteSettingsSection.BuildFeatures}>
          <SettingsCardTitle>
            {SiteSettingsSectionLabel[SiteSettingsSection.BuildFeatures]}
          </SettingsCardTitle>
          <SettingsCardContent css={cardCss}>
            <React.Fragment>
              <IncrementalBuildsSettings
                siteId={siteId}
                organizationId={organizationId}
                incrementalBuildsEnabled={siteData.incrementalBuildsEnabled}
              />
              <EnableBuildChecks
                siteId={siteId}
                buildChecksEnabled={siteData.buildChecksEnabled}
              />
              <EnableAutomaticDeploys
                siteId={siteId}
                organizationId={organizationId}
                manualHostingDeploysEnabled={
                  siteData.manualHostingDeploysEnabled
                }
                gatsbyHostingOn={gatsbyHostingOn}
              />
              {tier === MachinePricingTier.Enterprise && (
                <EnableIncrementalDeploysProduction
                  siteId={siteId}
                  enabled={siteData.incrementalDeploysProductionEnabled}
                />
              )}
              {flags.enableBurstModeToggle && (
                <EnableBurstBuilds
                  siteId={siteId}
                  organizationId={organizationId}
                  burstModeEnabled={siteData.burstModeEnabled}
                />
              )}
              {flags.enableMerlinPullRequestsSourcingToggle && (
                <EnableMerlinPullRequestsSourcing
                  siteId={siteId}
                  organizationId={organizationId}
                  enabled={siteData.merlinPullRequestsSourcingEnabled}
                />
              )}
              {flags.enableGatsbyjsioToggle && (
                <EnableGatsbyjsio
                  siteId={siteId}
                  organizationId={organizationId}
                  enabled={siteData.gatsbyjsioEnabled}
                />
              )}
              {flags.enableSlicesApiToggle && (
                <EnableSlicesAPI
                  siteId={siteId}
                  organizationId={organizationId}
                  enabled={siteData.slicesAPIEnabled}
                  fastHostingEnabled={siteData.gatsbyjsioEnabled}
                />
              )}
            </React.Fragment>
          </SettingsCardContent>
        </SettingsCard>
      )}
    </React.Fragment>
  )
}

BuildsSection.propTypes = propTypes
